import { useState, useEffect, useCallback, useRef } from "react";
import { fetchSnapshot } from "../services/snapshotService";

// ==========================================
// 🎯 測試設定區（在這裡調整測試模式）
// ==========================================

// 1️⃣ 是否使用 Mock 資料（開發測試用）
const USE_MOCK_DATA = false; // true = 使用 mock 資料, false = 呼叫真實 API

// 2️⃣ 選擇 Mock 資料版本（當 USE_MOCK_DATA = true 時生效）
// 'full'          = 完整資料（所有欄位都有真實數據）
// 'no_data'       = 所有欄位都是 'no data'（測試「此項目暫無數據」的顯示）
// 'mixed'         = 混合版本（部分有資料、部分無資料）
// 'empty_resource' = 完全無資料（模擬 API 回傳 _noData: true 的情境）
const MOCK_DATA_MODE = "full"; // ⬅️ 在這裡切換測試模式

// ==========================================

// 📦 Mock 資料 (範例回傳資料 - 完整資料版本)
const MOCK_SNAPSHOT_DATA_FULL = {
  propertyDisplayName: "台大魔法科學班｜冬令營｜夏令營 - GA4", // 客戶名稱（用於測試標題對齊）
  industry_category: "SHOPPING",
  behide_ratio: 0.04934821634560406,
  six_forces_raw: {
    newUsers: 35058,
    totalUsers: 47972,
    engagedSessions: 21700,
    addToCarts: 604,
    sessions: 51797,
    purchaseRevenue: 236169.99997300003,
    transactions: 66,
    firstTimePurchasers: 60,
    totalPurchasers: 66,
  },
  six_forces: {
    newUserRate: 5,
    bounceRate: 5,
    engagementRate: 1,
    addToCartRate: 1,
    purchaseRate: 1,
    firstTimePurchaserRate: 5,
  },
  six_forces_ratio: {
    newUserRate: 0.4823017102005813,
    bounceRate: 0.31104566163006836,
    engagementRate: 0.6889543383699316,
    addToCartRate: 0.0926342246272001,
    purchaseRate: 0.016926045143058482,
    firstTimePurchaserRate: 0.4489745662540929,
  },
  行業內綜合評分: 60,
  評估調整後評分: 80,
  active_users_region: {
    "Taichung City": 10553,
    "Taipei City": 10157,
    "Kaohsiung City": 5926,
    "New Taipei City": 5474,
    "Taoyuan City": 3636,
    "Tainan City": 1711,
    "Changhua County": 1425,
    "Hsinchu City": 962,
    "Hsinchu County": 617,
    "Keelung City": 566,
    "Pingtung County": 443,
    "Miaoli County": 350,
    "Yilan County": 340,
    "Yunlin County": 340,
    "Chiayi County": 283,
    "Nantou County": 249,
    "Hualien County": 219,
    "Chiayi City": 129,
    "Taitung County": 75,
    "Penghu County": 31,
    "(not set)": 26,
    "Kinmen County": 7,
  },
  newUserRate: 0.7308013007587759,
  oldUserRate: 0.2691986992412241,
  newUsers: 35058,
  oldUsers: 12914,
  即時轉換率: 0.0014187751241428233,
  完成一筆轉換需要: 727,
  churn_rate: {
    view_item: 0.9043831552698898,
    add_to_cart: 0.9566097122302158,
    begin_checkout: 0.4455958549222798,
    purchase: 0.383177570093458,
  },
  churn_users: {
    session_start: 43971,
    view_item: 4181,
    add_to_cart: 187,
    begin_checkout: 100,
    purchase: 66,
  },
  max_churn_event: "add_to_cart",
  裝置的使用者人數差距: {
    mobile: { totalUsers: 3860 },
    tablet: { totalUsers: 54 },
    desktop: { totalUsers: 267 },
  },
  tier: {
    google: 1,
    meta: 1,
    onead: 1,
    explorerbanner: 3,
    dbm: 2,
    signinad: 2,
    dcard: 1,
    litvinsteam: 1,
    line: 2,
    sms: 1,
    tamedia: 1,
    happygo: 2,
    spectrum: 3,
    an: 1,
    yahoo: 2,
    clickforce: 1,
    invos: 1,
    ad2: 1,
    tagtoo: 1,
    "cbwn.com.tw": 2,
    bing: 2,
    mababy: 2,
    invoiceman: 1,
    "imftw.my.salesforce-sites.com": 2,
    th: 1,
    huangmom: 2,
    qrcode: 2,
    edm: 2,
    "rabbitfunaround.com": 2,
    "huangmomtaiwan.com": 1,
    web: 1,
    mgm: 2,
    mombaby: 1,
    "wyethclub.wnservice.com.tw": 2,
    "acts.mababy.com": 3,
    "any-su.com": 2,
    "starthealthy.nestle.com.tw": 1,
    lifiapp: 2,
    "kodomoshops.com": 1,
    "vocus.cc": 2,
    baidu: 2,
    blog: 3,
    "chatgpt.com": 1,
    "mama.mummy.com.tw": 3,
    "statics.teams.cdn.office.net": 1,
    "emap.presco.com.tw": 2,
    "homekid.tw": 2,
    "mamibuy.com.tw": 1,
    mummy: 1,
    "payment.ecpay.com.tw": 2,
    retrievertripblog: 3,
    "reurl.cc": 1,
    tenmax: 1,
    "cathay-cube.com.tw": 1,
    mamibuy: 3,
    "ntp.msn.com": 3,
    "wyeth.wnservice.com.tw": 2,
    "taaaci.org.tw": 3,
    abandoned_cart: 1,
    "event.ad.megais.com": 3,
    "gwan.tw": 3,
    igbio: 2,
    "m.baidu.com": 1,
    "shinan-drugstore.com.tw": 1,
    superwall: 3,
    "musesingingstudio.com": 3,
    popin: 1,
    "prcube.planfix.com": 1,
    "shopify.com": 1,
    "vnb.com.tw": 1,
    "xincoupon.com": 3,
    adbert: 3,
    "app.neuronwriter.com": 3,
    "bit.ly": 3,
    "canva.com": 3,
    crm: 3,
    "dcwds.com.tw": 1,
    "dcwn.com.tw": 3,
    "dmcvienna.atlassian.net": 3,
    dsb: 1,
    dv360: 1,
    "ecosia.org": 3,
    "forum.babyhome.com.tw": 1,
    gt: 3,
    "hitachigroup.okta.com": 3,
    "ibmm.tw": 1,
    "kengfong.com.tw": 3,
    "maternity-baby-expo": 1,
    medical: 3,
    "mombaby.com.tw": 1,
    "nescafe-dolcegusto.com.br": 1,
    "nestlebaby.com.tw": 1,
    ptt: 1,
    shopify: 3,
    ttd: 1,
    "utm_source=basketreminder": 3,
    "vocus-cc.cdn.ampproject.org": 3,
    "websearch.rakuten.co.jp": 3,
    yandex: 1,
    "yannigo.com": 3,
    "yourator.co": 3,
  },
};

// 📦 Mock 資料 (所有欄位都是 'no data' 的測試版本)
const MOCK_SNAPSHOT_DATA_NO_DATA = {
  propertyDisplayName: "測試商店 - GA4", // 客戶名稱（短名稱測試）
  industry_category: "no data",
  behide_ratio: "no data", // ❌ 無資料：落後產業百分比
  six_forces_raw: "no data", // ❌ 無資料
  six_forces: "no data", // ❌ 無資料：雷達圖
  six_forces_ratio: "no data", // ❌ 無資料：六力比率
  行業內綜合評分: "no data", // ❌ 無資料：行業內綜合評分
  評估調整後評分: "no data", // ❌ 無資料：評估調整後評分
  active_users_region: "no data", // ❌ 無資料：台灣地圖
  newUserRate: "no data", // ❌ 無資料：新客率
  oldUserRate: "no data", // ❌ 無資料：舊客率
  newUsers: "no data",
  oldUsers: "no data",
  即時轉換率: "no data", // ❌ 無資料：即時轉換率
  完成一筆轉換需要: "no data", // ❌ 無資料
  churn_rate: "no data", // ❌ 無資料
  churn_users: "no data", // ❌ 無資料：行銷漏斗
  max_churn_event: "no data", // ❌ 無資料：最大流失環節
  裝置的使用者人數差距: "no data", // ❌ 無資料：裝置流失分佈
  tier: "no data", // ❌ 無資料：渠道表現表格
};

// 📦 Mock 資料 (混合版本：部分有資料、部分無資料)
const MOCK_SNAPSHOT_DATA_MIXED = {
  propertyDisplayName: "混合測試資料 - GA4", // 客戶名稱（混合版本測試用）
  industry_category: "SHOPPING",
  behide_ratio: 0.04934821634560406, // ✅ 有資料
  six_forces_raw: {
    newUsers: 35058,
    totalUsers: 47972,
    engagedSessions: 21700,
    addToCarts: 604,
    sessions: 51797,
    purchaseRevenue: 236169.99997300003,
    transactions: 66,
    firstTimePurchasers: 60,
    totalPurchasers: 66,
  },
  six_forces: {
    newUserRate: 5,
    bounceRate: 5,
    engagementRate: 1,
    addToCartRate: 1,
    purchaseRate: 1,
    firstTimePurchaserRate: 5,
  }, // ✅ 有資料：雷達圖正常顯示
  six_forces_ratio: {
    newUserRate: 0.4823017102005813,
    bounceRate: 0.31104566163006836,
    engagementRate: 0.6889543383699316,
    addToCartRate: 0.0926342246272001,
    purchaseRate: 0.016926045143058482,
    firstTimePurchaserRate: 0.4489745662540929,
  }, // ✅ 有資料：六力比率
  行業內綜合評分: 60, // ✅ 有資料
  評估調整後評分: 80, // ✅ 有資料
  active_users_region: "no data", // ❌ 無資料：台灣地圖
  newUserRate: 0.7308013007587759, // ✅ 有資料
  oldUserRate: 0.2691986992412241,
  newUsers: 35058,
  oldUsers: 12914,
  即時轉換率: 0.0014187751241428233, // ✅ 有資料
  完成一筆轉換需要: 727, // ✅ 有資料
  churn_rate: {
    view_item: 0.9043831552698898,
    add_to_cart: 0.9566097122302158,
    begin_checkout: 0.4455958549222798,
    purchase: 0.383177570093458,
  }, // ✅ 有資料
  churn_users: {
    session_start: 43971,
    view_item: 4181,
    add_to_cart: 187,
    begin_checkout: 100,
    purchase: 66,
  }, // ✅ 有資料：行銷漏斗正常顯示
  max_churn_event: "add_to_cart", // ✅ 有資料
  裝置的使用者人數差距: "no data", // ❌ 無資料：裝置流失分佈
  tier: "no data", // ❌ 無資料：渠道表現表格
};

// 📦 Mock 資料 (完全無資料：模擬 API 回傳 _noData: true 的情境)
const MOCK_SNAPSHOT_DATA_EMPTY_RESOURCE = {
  _noData: true, // 🚫 標記為完全無資料
  propertyDisplayName: "無資料測試資源 - GA4",
};

// 🎯 根據 MOCK_DATA_MODE 選擇對應的資料
const MOCK_SNAPSHOT_DATA =
  MOCK_DATA_MODE === "full"
    ? MOCK_SNAPSHOT_DATA_FULL
    : MOCK_DATA_MODE === "no_data"
      ? MOCK_SNAPSHOT_DATA_NO_DATA
      : MOCK_DATA_MODE === "empty_resource"
        ? MOCK_SNAPSHOT_DATA_EMPTY_RESOURCE
        : MOCK_SNAPSHOT_DATA_MIXED;

/**
 * Snapshot 輪詢 Hook
 *
 * 功能:
 * 1. 當 email 和 propertyId 都有值時,自動呼叫 API
 * 2. 成功後啟動 15 分鐘倒數計時
 * 3. 倒數到 0 時自動重新呼叫 API
 * 4. 提供手動 refetch 功能
 * 5. 組件卸載時自動清除 timer
 *
 * 🎯 開發模式:
 * - USE_MOCK_DATA = true: 使用範例資料,不呼叫 API
 * - USE_MOCK_DATA = false: 呼叫真實 API
 *
 * @param {string} email - 使用者 email
 * @param {string} propertyId - GA4 Property ID
 * @param {boolean} isPaused - 是否暫停倒數
 * @returns {Object} { data, loading, error, remainingSeconds, refetch }
 */
const useSnapshotPolling = (email, propertyId, isPaused = false) => {
  // 狀態管理
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0); // 0 表示尚未開始

  // 使用 ref 儲存 timer,避免閉包問題
  const countdownTimerRef = useRef(null);
  const pollingTimerRef = useRef(null);

  /**
   * 清除所有 timer
   */
  const clearAllTimers = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  /**
   * 呼叫 API 取得資料
   */
  const fetchData = useCallback(async () => {
    // 參數驗證 (生產模式才需要)
    if (!USE_MOCK_DATA && (!email || !propertyId)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let result;

      if (USE_MOCK_DATA) {
        // 🎯 開發模式: 使用 mock data
        console.log("📦 Using MOCK data (not calling API):", {
          email,
          propertyId,
          timestamp: new Date().toISOString(),
        });

        // 模擬 API 延遲 (500ms)
        await new Promise((resolve) => setTimeout(resolve, 500));

        result = MOCK_SNAPSHOT_DATA;
      } else {
        // 🚀 生產模式: 呼叫真實 API
        console.log("🚀 Calling real API:", {
          email,
          propertyId,
          timestamp: new Date().toISOString(),
        });

        result = await fetchSnapshot(email, propertyId);
      }

      setData(result);
      setError(null);

      // 成功後啟動 15 分鐘倒數 (900 秒)
      setRemainingSeconds(900);

      console.log("✅ Snapshot data loaded successfully");
    } catch (err) {
      setError(err);
      console.error("❌ useSnapshotPolling: Failed to fetch snapshot", {
        email,
        propertyId,
        error: err.message,
      });
      // 錯誤時不啟動倒數,讓使用者可以手動重試
    } finally {
      setLoading(false);
    }
  }, [email, propertyId]);

  /**
   * 手動重新取得資料
   */
  const refetch = useCallback(() => {
    clearAllTimers();
    fetchData();
  }, [fetchData, clearAllTimers]);

  /**
   * 🎯 自動載入資料 (開發模式不需要 email 和 propertyId)
   */
  useEffect(() => {
    if (USE_MOCK_DATA) {
      // 開發模式: 直接載入 mock data
      fetchData();
    } else {
      // 生產模式: 需要 email 和 propertyId
      if (email && propertyId) {
        clearAllTimers();
        setRemainingSeconds(0);
        fetchData();
      }
    }

    // 組件卸載時清除 timer
    return () => {
      clearAllTimers();
    };
  }, [email, propertyId, fetchData, clearAllTimers]);

  /**
   * 倒數計時邏輯
   * 每秒減 1,倒數到 0 時自動 refetch
   * 當 isPaused 為 true 時暫停倒數
   */
  useEffect(() => {
    // 只有當 remainingSeconds > 0 且未暫停時才啟動倒數
    if (remainingSeconds > 0 && !isPaused) {
      countdownTimerRef.current = setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            // 倒數到 0,觸發重新取得資料
            // 使用 setTimeout 避免在 state 更新中呼叫其他 state 更新
            pollingTimerRef.current = setTimeout(() => {
              refetch();
            }, 0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000); // 每秒更新一次

      // 清理函數
      return () => {
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
      };
    }
  }, [remainingSeconds, refetch, isPaused]);

  return {
    data, // API 回傳資料
    loading, // 載入狀態
    error, // 錯誤訊息
    remainingSeconds, // 剩餘秒數 (0-900)
    refetch, // 手動重新取得函數
  };
};

export default useSnapshotPolling;
